// A road tile for vehicle traffic, ported from age-of-mcp's avenue.ts. The game
// warps each avenue tile to a shared road height field and autotiles its lane
// markings from which neighbours are road; the model library has no terrain, so
// this port renders the FLAT, lone-tile look the flattened gallery baked: a flat
// asphalt slab with a white shoulder line on each of the four open edges (no
// neighbours connected, so no centre line). The slab and markings are still
// described by the source's `warpedSlab`/`mark`/`edgeLines` helpers, specialised
// to a flat surface (FLAT corners, zero tilt) instead of inlined vertex soup.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const ASPHALT = "#3a3a3a";
const LINE_WHITE = "#e8e8d0";   // lane-edge lines (road shoulders)

const HALF = 0.5;
const SLAB_H = 0.12;    // asphalt thickness
const LIFT = 0.025;     // lift the road a sliver proud of the terrain
const EPS = 0.004;      // lift markings just above the asphalt

// Connectivity uses 4 orthogonal bits (WALL_DIRS encoding): E=0, N=2, W=4, S=6.
const N = 2, S = 6, E = 0, W = 4;

// A flat horizontal slab — `warpedSlab` with FLAT corners. Top at `lift`, bottom
// at `-thickness`, baked into the vertices like the source. Place at the origin.
function flatSlab(thickness: number, color: string, lift: number): Part {
  const top = lift;
  const yb = -thickness;
  const h = HALF;
  type V = readonly [number, number, number];
  const tSW: V = [-h, top, -h], tSE: V = [h, top, -h];
  const tNW: V = [-h, top, h], tNE: V = [h, top, h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb, h], bNE: V = [h, yb, h];
  const pos: number[] = [];
  const quad = (p0: V, p1: V, p2: V, p3: V): void => { pos.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3); };
  quad(tSW, tNW, tNE, tSE);   // top    (+Y)
  quad(bSW, bSE, bNE, bNW);   // bottom (-Y)
  quad(tNW, bNW, bNE, tNE);   // north  (+Z)
  quad(tSE, bSE, bSW, tSW);   // south  (-Z)
  quad(tSE, tNE, bNE, bSE);   // east   (+X)
  quad(tSW, bSW, bNW, tNW);   // west   (-X)
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// Lay a thin marking flush ON the (flat) road surface at local (lx, lz): a tall
// box buried in the asphalt whose top sits a sliver above the deck.
function mark(lx: number, lz: number, sx: number, sz: number, color: string): Part {
  const h = SLAB_H + EPS;
  return {
    geometry: new THREE.BoxGeometry(sx, h, sz),
    color,
    position: [lx, LIFT + EPS - h / 2, lz],
    eulerOrder: "ZYX",
  };
}

// White edge lines (road shoulders) on every side that does NOT connect — for a
// lone tile (no connections), all four sides.
function edgeLines(connections: number): Part[] {
  const has = (bit: number): boolean => (connections & (1 << bit)) !== 0;
  const out: Part[] = [];
  const w = 0.035, o = HALF - 0.04;
  if (!has(N)) out.push(mark(0, o, HALF * 2, w, LINE_WHITE));
  if (!has(S)) out.push(mark(0, -o, HALF * 2, w, LINE_WHITE));
  if (!has(E)) out.push(mark(o, 0, w, HALF * 2, LINE_WHITE));
  if (!has(W)) out.push(mark(-o, 0, w, HALF * 2, LINE_WHITE));
  return out;
}

function partsFor(s: number): Part[] {
  // Flat, lone tile: connections = 0 (no neighbour roads), no crosswalk.
  if (s <= 0) return [flatSlab(0.1, "#5a5050", LIFT * 0.6)];
  const slab = flatSlab(SLAB_H, ASPHALT, LIFT);
  if (s === 1) return [slab];
  // A lone tile has no connections, so `centreMarks` is empty; only the four
  // white shoulder lines remain.
  return [slab, ...edgeLines(0)];
}

export const avenue = defineModel({
  id: "avenue",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Avenue",
  tags: ["building","avenue","low-poly","age-of-mcp","classic"],
  summary: "Low-poly avenue.",
  description: "A road tile for vehicle traffic.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
